import React, { useState, useContext, useMemo } from 'react';
import { Dropdown } from 'react-bootstrap';
import DatePicker, { registerLocale } from 'react-datepicker';
import es from 'date-fns/locale/es';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faCalendarAlt,
  faChevronDown,
  faCalendarDay,
  faCalendarWeek,
  faCheck
} from '@fortawesome/free-solid-svg-icons';
import AppContext from 'context/Context';

registerLocale('es', es);

export const formatDateToYMD = date => {
  if (!date) return null;
  const d = new Date(date);
  if (isNaN(d.getTime())) return null;
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const TicketsDateFilter = ({
  dateFilter,
  onChangeDateFilter
}) => {
  const { config: { isDark } = {} } = useContext(AppContext);
  const [isOpen, setIsOpen] = useState(false);
  const [activeTab, setActiveTab] = useState(dateFilter?.mode === 'rango' ? 'rango' : 'especifica');

  // Fechas temporales para selección de rango interactivo
  const [rangeStart, setRangeStart] = useState(dateFilter?.startDate ? new Date(dateFilter.startDate) : new Date());
  const [rangeEnd, setRangeEnd] = useState(dateFilter?.endDate ? new Date(dateFilter.endDate) : new Date());

  const currentMode = dateFilter?.mode || 'hoy';
  const startDate = dateFilter?.startDate ? new Date(dateFilter.startDate) : null;
  const endDate = dateFilter?.endDate ? new Date(dateFilter.endDate) : null;

  // Formato del texto que se muestra en el botón trigger
  const labelText = useMemo(() => {
    if (currentMode === 'hoy') {
      const today = new Date();
      return `Hoy: ${today.toLocaleDateString('es-MX', { day: 'numeric', month: 'short' })}`;
    }
    if (currentMode === 'ayer') {
      const yesterday = new Date();
      yesterday.setDate(yesterday.getDate() - 1);
      return `Ayer: ${yesterday.toLocaleDateString('es-MX', { day: 'numeric', month: 'short' })}`;
    }
    if (currentMode === '7dias') {
      return 'Últimos 7 días';
    }
    if (currentMode === 'mes') {
      return 'Este mes';
    }
    if (currentMode === 'todos') {
      return 'Histórico completo';
    }
    if (currentMode === 'especifica' && startDate) {
      return startDate.toLocaleDateString('es-MX', { day: 'numeric', month: 'short', year: 'numeric' });
    }
    if (currentMode === 'rango' && startDate && endDate) {
      const startStr = startDate.toLocaleDateString('es-MX', { day: 'numeric', month: 'short' });
      const endStr = endDate.toLocaleDateString('es-MX', { day: 'numeric', month: 'short', year: 'numeric' });
      return `${startStr} - ${endStr}`;
    }
    return 'Filtrar por fecha';
  }, [currentMode, startDate, endDate]);

  // Manejador para aplicar presets rápidos
  const applyPreset = mode => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);

    if (mode === 'hoy') {
      onChangeDateFilter({
        mode: 'hoy',
        startDate: today,
        endDate: today,
        dFechaInicio: formatDateToYMD(today),
        dFechaFin: formatDateToYMD(today)
      });
      setRangeStart(today);
      setRangeEnd(today);
      setActiveTab('especifica');
      setIsOpen(false);
      return;
    }

    if (mode === 'ayer') {
      const yesterday = new Date(today);
      yesterday.setDate(yesterday.getDate() - 1);
      onChangeDateFilter({
        mode: 'ayer',
        startDate: yesterday,
        endDate: yesterday,
        dFechaInicio: formatDateToYMD(yesterday),
        dFechaFin: formatDateToYMD(yesterday)
      });
      setRangeStart(yesterday);
      setRangeEnd(yesterday);
      setActiveTab('especifica');
      setIsOpen(false);
      return;
    }

    if (mode === '7dias') {
      const start7 = new Date(today);
      start7.setDate(start7.getDate() - 6);
      onChangeDateFilter({
        mode: '7dias',
        startDate: start7,
        endDate: today,
        dFechaInicio: formatDateToYMD(start7),
        dFechaFin: formatDateToYMD(today)
      });
      setRangeStart(start7);
      setRangeEnd(today);
      setActiveTab('rango');
      setIsOpen(false);
      return;
    }

    if (mode === 'mes') {
      const startMonth = new Date(today.getFullYear(), today.getMonth(), 1);
      onChangeDateFilter({
        mode: 'mes',
        startDate: startMonth,
        endDate: today,
        dFechaInicio: formatDateToYMD(startMonth),
        dFechaFin: formatDateToYMD(today)
      });
      setRangeStart(startMonth);
      setRangeEnd(today);
      setActiveTab('rango');
      setIsOpen(false);
      return;
    }

    if (mode === 'todos') {
      onChangeDateFilter({
        mode: 'todos',
        startDate: null,
        endDate: null,
        dFechaInicio: null,
        dFechaFin: null
      });
      setIsOpen(false);
      return;
    }
  };

  // Manejador para selección de fecha específica individual
  const handleSingleDateChange = date => {
    if (!date) return;
    const selected = new Date(date);
    selected.setHours(0, 0, 0, 0);

    const todayYMD = formatDateToYMD(new Date());
    const selectedYMD = formatDateToYMD(selected);

    onChangeDateFilter({
      mode: selectedYMD === todayYMD ? 'hoy' : 'especifica',
      startDate: selected,
      endDate: selected,
      dFechaInicio: selectedYMD,
      dFechaFin: selectedYMD
    });
    setIsOpen(false);
  };

  // Manejador para selección interactiva de rango de fechas
  const handleRangeChange = dates => {
    const [start, end] = dates;
    setRangeStart(start);
    setRangeEnd(end);

    if (start && end) {
      onChangeDateFilter({
        mode: 'rango',
        startDate: start,
        endDate: end,
        dFechaInicio: formatDateToYMD(start),
        dFechaFin: formatDateToYMD(end)
      });
      setIsOpen(false);
    }
  };

  const isFiltered = currentMode !== 'todos';

  return (
    <div className="se-tickets-date-filter">
      <Dropdown show={isOpen} onToggle={open => setIsOpen(open)} autoClose="outside">
        <Dropdown.Toggle
          as="button"
          type="button"
          className="btn btn-sm d-flex align-items-center gap-2 border shadow-none px-2 py-1 rounded-2 transition-base dropdown-caret-none"
          style={{
            backgroundColor: isDark ? '#121e2d' : '#ffffff',
            borderColor: isFiltered
              ? (isDark ? '#38bdf8' : '#0284c7')
              : (isDark ? '#233142' : '#d8e2ef'),
            color: isFiltered
              ? (isDark ? '#38bdf8' : '#0284c7')
              : (isDark ? '#cbd5e1' : '#475569'),
            fontSize: '0.82rem',
            fontWeight: isFiltered ? 600 : 500,
            cursor: 'pointer'
          }}
          title="Filtrar lista de tickets por fecha"
        >
          <FontAwesomeIcon
            icon={faCalendarAlt}
            style={{ color: isFiltered ? (isDark ? '#38bdf8' : '#0284c7') : (isDark ? '#94a3b8' : '#64748b') }}
          />
          <span className="text-truncate" style={{ maxWidth: '200px' }}>
            {labelText}
          </span>
          <FontAwesomeIcon
            icon={faChevronDown}
            className="text-400 ms-1"
            style={{ fontSize: '0.70rem' }}
          />
        </Dropdown.Toggle>

        <Dropdown.Menu
          align="end"
          className="shadow-lg border p-3"
          style={{
            minWidth: '310px',
            maxWidth: '350px',
            width: 'calc(100vw - 36px)',
            backgroundColor: isDark ? '#121e2d' : '#ffffff',
            borderColor: isDark ? '#233142' : '#e2e8f0',
            color: isDark ? '#f9fafd' : '#0f172a',
            zIndex: 1050
          }}
        >
          {/* Encabezado del Dropdown */}
          <div className="d-flex align-items-center justify-content-between pb-2 mb-2 border-bottom" style={{ borderColor: isDark ? '#233142' : '#e2e8f0' }}>
            <div className="d-flex align-items-center gap-2">
              <FontAwesomeIcon icon={faCalendarAlt} style={{ color: isDark ? '#38bdf8' : '#0284c7' }} />
              <span className="fw-bold" style={{ fontSize: '0.86rem' }}>
                Filtrar por fecha
              </span>
            </div>
            {currentMode !== 'hoy' && (
              <button
                type="button"
                onClick={() => applyPreset('hoy')}
                className="btn btn-link p-0 text-decoration-none"
                style={{ fontSize: '0.75rem', color: isDark ? '#38bdf8' : '#0284c7' }}
              >
                Restablecer a hoy
              </button>
            )}
          </div>

          {/* Accesos directos / Presets */}
          <div className="mb-2">
            <div className="text-uppercase fw-semibold mb-1" style={{ fontSize: '0.66rem', color: isDark ? '#94a3b8' : '#64748b' }}>
              Accesos rápidos
            </div>
            <div className="d-flex flex-wrap gap-1">
              <button
                type="button"
                onClick={() => applyPreset('hoy')}
                className={`btn btn-sm px-2 py-1 border-0 rounded-2 transition-base ${currentMode === 'hoy' ? 'fw-bold' : ''}`}
                style={{
                  fontSize: '0.76rem',
                  backgroundColor: currentMode === 'hoy'
                    ? (isDark ? '#0369a1' : '#e0f2fe')
                    : (isDark ? '#1e293b' : '#f1f5f9'),
                  color: currentMode === 'hoy'
                    ? (isDark ? '#ffffff' : '#0369a1')
                    : (isDark ? '#cbd5e1' : '#475569')
                }}
              >
                {currentMode === 'hoy' && <FontAwesomeIcon icon={faCheck} className="me-1" />}
                Hoy
              </button>

              <button
                type="button"
                onClick={() => applyPreset('ayer')}
                className={`btn btn-sm px-2 py-1 border-0 rounded-2 transition-base ${currentMode === 'ayer' ? 'fw-bold' : ''}`}
                style={{
                  fontSize: '0.76rem',
                  backgroundColor: currentMode === 'ayer'
                    ? (isDark ? '#0369a1' : '#e0f2fe')
                    : (isDark ? '#1e293b' : '#f1f5f9'),
                  color: currentMode === 'ayer'
                    ? (isDark ? '#ffffff' : '#0369a1')
                    : (isDark ? '#cbd5e1' : '#475569')
                }}
              >
                {currentMode === 'ayer' && <FontAwesomeIcon icon={faCheck} className="me-1" />}
                Ayer
              </button>

              <button
                type="button"
                onClick={() => applyPreset('7dias')}
                className={`btn btn-sm px-2 py-1 border-0 rounded-2 transition-base ${currentMode === '7dias' ? 'fw-bold' : ''}`}
                style={{
                  fontSize: '0.76rem',
                  backgroundColor: currentMode === '7dias'
                    ? (isDark ? '#0369a1' : '#e0f2fe')
                    : (isDark ? '#1e293b' : '#f1f5f9'),
                  color: currentMode === '7dias'
                    ? (isDark ? '#ffffff' : '#0369a1')
                    : (isDark ? '#cbd5e1' : '#475569')
                }}
              >
                {currentMode === '7dias' && <FontAwesomeIcon icon={faCheck} className="me-1" />}
                Últimos 7 días
              </button>

              <button
                type="button"
                onClick={() => applyPreset('mes')}
                className={`btn btn-sm px-2 py-1 border-0 rounded-2 transition-base ${currentMode === 'mes' ? 'fw-bold' : ''}`}
                style={{
                  fontSize: '0.76rem',
                  backgroundColor: currentMode === 'mes'
                    ? (isDark ? '#0369a1' : '#e0f2fe')
                    : (isDark ? '#1e293b' : '#f1f5f9'),
                  color: currentMode === 'mes'
                    ? (isDark ? '#ffffff' : '#0369a1')
                    : (isDark ? '#cbd5e1' : '#475569')
                }}
              >
                {currentMode === 'mes' && <FontAwesomeIcon icon={faCheck} className="me-1" />}
                Este mes
              </button>

              <button
                type="button"
                onClick={() => applyPreset('todos')}
                className={`btn btn-sm px-2 py-1 border-0 rounded-2 transition-base ${currentMode === 'todos' ? 'fw-bold' : ''}`}
                style={{
                  fontSize: '0.76rem',
                  backgroundColor: currentMode === 'todos'
                    ? (isDark ? '#0369a1' : '#e0f2fe')
                    : (isDark ? '#1e293b' : '#f1f5f9'),
                  color: currentMode === 'todos'
                    ? (isDark ? '#ffffff' : '#0369a1')
                    : (isDark ? '#cbd5e1' : '#475569')
                }}
              >
                {currentMode === 'todos' && <FontAwesomeIcon icon={faCheck} className="me-1" />}
                Histórico completo
              </button>
            </div>
          </div>

          {/* Selector de modo personalizado: Fecha específica vs Rango */}
          <div className="p-1 rounded-2 mb-2 d-flex gap-1" style={{ backgroundColor: isDark ? '#0b1727' : '#f1f5f9' }}>
            <button
              type="button"
              onClick={() => setActiveTab('especifica')}
              className="btn btn-sm flex-fill border-0 rounded-2 py-1 transition-base d-flex align-items-center justify-content-center gap-1"
              style={{
                fontSize: '0.74rem',
                fontWeight: activeTab === 'especifica' ? 700 : 500,
                backgroundColor: activeTab === 'especifica'
                  ? (isDark ? '#121e2d' : '#ffffff')
                  : 'transparent',
                color: activeTab === 'especifica'
                  ? (isDark ? '#f9fafd' : '#0f172a')
                  : (isDark ? '#94a3b8' : '#64748b'),
                boxShadow: activeTab === 'especifica'
                  ? (isDark ? '0 1px 3px rgba(0,0,0,0.4)' : '0 1px 2px rgba(0,0,0,0.08)')
                  : 'none'
              }}
            >
              <FontAwesomeIcon icon={faCalendarDay} style={{ fontSize: '0.70rem' }} />
              <span>Fecha específica</span>
            </button>

            <button
              type="button"
              onClick={() => setActiveTab('rango')}
              className="btn btn-sm flex-fill border-0 rounded-2 py-1 transition-base d-flex align-items-center justify-content-center gap-1"
              style={{
                fontSize: '0.74rem',
                fontWeight: activeTab === 'rango' ? 700 : 500,
                backgroundColor: activeTab === 'rango'
                  ? (isDark ? '#121e2d' : '#ffffff')
                  : 'transparent',
                color: activeTab === 'rango'
                  ? (isDark ? '#f9fafd' : '#0f172a')
                  : (isDark ? '#94a3b8' : '#64748b'),
                boxShadow: activeTab === 'rango'
                  ? (isDark ? '0 1px 3px rgba(0,0,0,0.4)' : '0 1px 2px rgba(0,0,0,0.08)')
                  : 'none'
              }}
            >
              <FontAwesomeIcon icon={faCalendarWeek} style={{ fontSize: '0.70rem' }} />
              <span>Rango de fechas</span>
            </button>
          </div>

          {/* Calendario interactivo inline */}
          <div className="d-flex justify-content-center my-1 se-datepicker-container">
            {activeTab === 'especifica' ? (
              <DatePicker
                selected={startDate || new Date()}
                onChange={handleSingleDateChange}
                inline
                locale="es"
                maxDate={new Date()}
              />
            ) : (
              <DatePicker
                selectsRange={true}
                startDate={rangeStart}
                endDate={rangeEnd}
                onChange={handleRangeChange}
                inline
                locale="es"
                maxDate={new Date()}
              />
            )}
          </div>

          {/* Leyenda y acciones inferiores */}
          <div className="pt-2 mt-1 border-top d-flex align-items-center justify-content-between flex-wrap gap-2" style={{ borderColor: isDark ? '#233142' : '#e2e8f0' }}>
            <div className="text-500 text-truncate" style={{ fontSize: '0.74rem', maxWidth: '200px' }}>
              {activeTab === 'rango' && (!rangeEnd || !rangeStart) ? (
                <span className="text-warning">Selecciona inicio y fin</span>
              ) : (
                <span>{labelText}</span>
              )}
            </div>

            <div className="d-flex align-items-center gap-1 ms-auto">
              <button
                type="button"
                onClick={() => setIsOpen(false)}
                className="btn btn-sm btn-secondary px-2 py-0 border-0"
                style={{ fontSize: '0.74rem' }}
              >
                Cerrar
              </button>
            </div>
          </div>
        </Dropdown.Menu>
      </Dropdown>
    </div>
  );
};

export default TicketsDateFilter;
